import { useEffect, useMemo, useRef, useState } from "react";
import { Image, ScrollView, Text, TextInput, View } from "react-native";
import {
  Building2,
  FileText,
  MapPin,
  Mail,
  Phone,
  Stethoscope,
} from "lucide-react-native";
import {
  ActionBanner,
  Dialog,
  DialogIcon,
  DialogPrimaryButton,
  MoonFieldInput,
  MoonFormField,
  Row,
  SoftSecondaryButton,
  TextActionButton,
  colors,
} from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import {
  useCreateCareConnection,
  useDeleteCareConnection,
  useUpdateCareConnection,
} from "@src/packages/patients/hooks/useCareConnections";
import type {
  CareConnection,
  CareConnectionKind,
} from "@src/packages/patients/hooks/useCareConnections";
import { EMBER } from "../../../../onboarding/theme";

type Props = {
  visible: boolean;
  kind: CareConnectionKind | null;
  connections: CareConnection[];
  selectedConnectionId?: string | null;
  icon: number;
  onClose: () => void;
};

type ModalView = "manage" | "form" | "confirm-delete" | "success";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function connectionKey(kind: CareConnectionKind) {
  return `you.connections_sharing.connection_modal.${kind}`;
}

function managementKey(kind: CareConnectionKind) {
  return `you.connections_sharing.connection_management.${kind}`;
}

/**
 * Manages patient-owned care-team records. Identity matching, connection, and
 * sharing are rendered as separate lifecycle concerns.
 */
export default function CareConnectionModal({
  visible,
  kind,
  connections,
  selectedConnectionId: initialConnectionId = null,
  icon,
  onClose,
}: Props) {
  const t = useTranslations();
  const createConnection = useCreateCareConnection();
  const updateConnection = useUpdateCareConnection();
  const deleteConnection = useDeleteCareConnection();
  const [view, setView] = useState<ModalView>("form");
  const [selectedConnectionId, setSelectedConnectionId] = useState<
    string | null
  >(null);
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [practiceName, setPracticeName] = useState("");
  const [location, setLocation] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [recentlySaved, setRecentlySaved] = useState<CareConnection | null>(
    null,
  );
  const [submitted, setSubmitted] = useState(false);
  const [actionError, setActionError] = useState<"save" | "delete" | null>(
    null,
  );
  const openedKindRef = useRef<CareConnectionKind | null>(null);

  const selectedConnection = useMemo(
    () =>
      selectedConnectionId
        ? (connections.find(
            (connection) => connection.id === selectedConnectionId,
          ) ?? null)
        : null,
    [connections, selectedConnectionId],
  );

  useEffect(() => {
    if (!visible || !kind) {
      openedKindRef.current = null;
      return;
    }
    if (openedKindRef.current === kind) return;
    openedKindRef.current = kind;
    const params =
      __DEV__ && typeof window !== "undefined"
        ? new URLSearchParams(window.location.search)
        : null;
    const devView = params?.get("dev_care_connection_view");
    const devConnectionId = params?.get("dev_care_connection_id");
    const requestedConnectionId = devConnectionId ?? initialConnectionId;
    const devConnection =
      connections.find(
        (connection) => connection.id === requestedConnectionId,
      ) ??
      connections[0] ??
      null;

    setSelectedConnectionId(
      devView === "edit" || devView === "delete" || initialConnectionId
        ? (devConnection?.id ?? null)
        : null,
    );
    setView(
      devView === "form"
        ? "form"
        : devView === "success" && devConnection
          ? "success"
          : devView === "delete" && devConnection
            ? "confirm-delete"
            : devView === "edit" && devConnection
              ? "form"
              : initialConnectionId && devConnection
                ? "form"
                : connections.length > 0
                  ? "manage"
                  : "form",
    );
    setActionError(null);
    setRecentlySaved(devView === "success" ? devConnection : null);
  }, [connections, initialConnectionId, kind, visible]);

  useEffect(() => {
    if (!visible || view !== "form") return;
    setName(selectedConnection?.name ?? "");
    setRole(selectedConnection?.role ?? "");
    setContactEmail(selectedConnection?.contactEmail ?? "");
    setSpecialty(selectedConnection?.specialty ?? "");
    setPracticeName(selectedConnection?.practiceName ?? "");
    setLocation(selectedConnection?.location ?? "");
    setContactPhone(selectedConnection?.contactPhone ?? "");
    setNotes(selectedConnection?.notes ?? "");
    setSubmitted(false);
    setActionError(null);
  }, [selectedConnection, view, visible]);

  const isSaving = createConnection.isPending || updateConnection.isPending;
  const isDeleting = deleteConnection.isPending;
  const isBusy = isSaving || isDeleting;
  const normalizedName = name.trim();
  const normalizedEmail = contactEmail.trim();
  const nameError =
    submitted && !normalizedName
      ? t("you.connections_sharing.connection_modal.name_required")
      : undefined;
  const emailError =
    normalizedEmail && !EMAIL_PATTERN.test(normalizedEmail)
      ? t("you.connections_sharing.connection_modal.email_invalid")
      : undefined;

  const fields = useMemo(() => {
    if (!kind) return null;
    const prefix = connectionKey(kind);
    return {
      title: t(`${prefix}.${selectedConnection ? "edit_title" : "title"}`),
      description: t(`${prefix}.description`),
      nameLabel: t(`${prefix}.name_label`),
      namePlaceholder: t(`${prefix}.name_placeholder`),
    };
  }, [kind, selectedConnection, t]);

  if (!kind || !fields) return null;

  const handleClose = () => {
    if (!isBusy) onClose();
  };

  const openForm = (connection: CareConnection | null) => {
    setSelectedConnectionId(connection?.id ?? null);
    setActionError(null);
    setRecentlySaved(null);
    setView("form");
  };

  const returnToManagement = () => {
    setSelectedConnectionId(null);
    setActionError(null);
    setRecentlySaved(null);
    setView("manage");
  };

  const handleSave = async () => {
    setSubmitted(true);
    setActionError(null);
    if (!normalizedName || emailError) return;

    const body = {
      name: normalizedName,
      role: role.trim() || null,
      contactEmail: normalizedEmail || null,
      specialty: specialty.trim() || null,
      practiceName: practiceName.trim() || null,
      location: location.trim() || null,
      contactPhone: contactPhone.trim() || null,
      notes: notes.trim() || null,
      addedVia: selectedConnection?.addedVia ?? "manual",
    };

    try {
      const savedConnection = selectedConnection
        ? await updateConnection.mutateAsync({
            connectionId: selectedConnection.id,
            body,
          })
        : await createConnection.mutateAsync({ kind, ...body });
      setRecentlySaved(savedConnection);
      setView("success");
    } catch {
      setActionError("save");
    }
  };

  const handleDelete = async () => {
    if (!selectedConnection) return;
    setActionError(null);
    try {
      await deleteConnection.mutateAsync(selectedConnection.id);
      returnToManagement();
    } catch {
      setActionError("delete");
    }
  };

  const renderManagement = () => {
    const prefix = managementKey(kind);
    return (
      <>
        <DialogIcon source={icon} width={50} height={58} />
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 20,
            lineHeight: 25,
            fontWeight: "600",
            color: EMBER.colors.text,
            textAlign: "center",
            marginTop: 8,
            marginBottom: 6,
          }}
        >
          {t(`${prefix}.title`)}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 14,
            lineHeight: 20,
            color: EMBER.colors.textLight,
            textAlign: "center",
            marginBottom: 18,
          }}
        >
          {t("you.connections_sharing.connection_management.description")}
        </Text>

        {connections.length > 0 ? (
          <ScrollView
            style={{ maxHeight: 264, marginBottom: 18 }}
            contentContainerStyle={{ gap: 8, paddingBottom: 2 }}
            showsVerticalScrollIndicator={false}
          >
            {connections.map((connection) => {
              const subtitle =
                connection.role ??
                connection.specialty ??
                connection.practiceName ??
                connection.location ??
                t("you.connections_sharing.care_team_member");
              return (
                <View
                  key={connection.id}
                  style={{
                    overflow: "hidden",
                    borderRadius: 12,
                    backgroundColor: "rgba(255,255,255,0.48)",
                  }}
                >
                  <Row
                    surface="plain"
                    leading={
                      <Image
                        source={icon}
                        resizeMode="contain"
                        style={{ width: 24, height: 24 }}
                      />
                    }
                    leadingSize={26}
                    title={connection.name}
                    subtitle={subtitle}
                    chevron
                    onPress={() => openForm(connection)}
                    action="care_connection_row"
                    accessibilityLabel={connection.name}
                    gap={10}
                    paddingHorizontal={14}
                    paddingVertical={11}
                    borderRadius={12}
                    titleNumberOfLines={1}
                    subtitleNumberOfLines={1}
                    titleStyle={{
                      fontSize: 14,
                      lineHeight: 18,
                      fontWeight: "500",
                      color: EMBER.colors.text,
                    }}
                    subtitleStyle={{
                      fontSize: 12,
                      lineHeight: 16,
                      color: EMBER.colors.textLight,
                    }}
                    style={{ borderRadius: 12 }}
                  />
                </View>
              );
            })}
          </ScrollView>
        ) : (
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 14,
              lineHeight: 20,
              color: EMBER.colors.textLight,
              textAlign: "center",
              marginBottom: 18,
            }}
          >
            {t(`${prefix}.empty`)}
          </Text>
        )}

        <DialogPrimaryButton
          label={t(`${prefix}.add`)}
          onPress={() => openForm(null)}
          fullWidth
        />
      </>
    );
  };

  const renderForm = () => (
    <ScrollView
      style={{ maxHeight: 520 }}
      showsVerticalScrollIndicator={false}
      contentContainerStyle={{ paddingBottom: 2 }}
    >
      <DialogIcon source={icon} width={50} height={58} />

      <Text
        className="font-dm-sans"
        style={{
          fontSize: 20,
          lineHeight: 25,
          fontWeight: "600",
          color: EMBER.colors.text,
          textAlign: "center",
          marginTop: 8,
          marginBottom: 6,
        }}
      >
        {fields.title}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 14,
          lineHeight: 20,
          color: EMBER.colors.textLight,
          textAlign: "center",
          marginBottom: 20,
        }}
      >
        {fields.description}
      </Text>

      <MoonFormField
        label={fields.nameLabel}
        labelIcon={kind === "provider" ? Stethoscope : Building2}
        error={nameError}
        required
      >
        <MoonFieldInput hasError={!!nameError}>
          <TextInput
            value={name}
            onChangeText={(value) => {
              setName(value);
              setActionError(null);
            }}
            accessibilityLabel={fields.nameLabel}
            placeholder={fields.namePlaceholder}
            placeholderTextColor={colors.textFaint}
            autoCapitalize="words"
            returnKeyType="next"
            editable={!isBusy}
            className="font-dm-sans"
            style={{
              minHeight: 44,
              paddingVertical: 10,
              fontSize: 14,
              color: colors.text,
            }}
          />
        </MoonFieldInput>
      </MoonFormField>

      {kind === "provider" ? (
        <MoonFormField
          label={t("you.connections_sharing.connection_modal.relationship")}
          labelIcon={Stethoscope}
          style={{ marginTop: 16 }}
        >
          <MoonFieldInput>
            <TextInput
              value={role}
              onChangeText={setRole}
              accessibilityLabel={t(
                "you.connections_sharing.connection_modal.relationship",
              )}
              placeholder={t(
                "you.connections_sharing.connection_modal.relationship_placeholder",
              )}
              placeholderTextColor={colors.textFaint}
              autoCapitalize="words"
              returnKeyType="next"
              editable={!isBusy}
              className="font-dm-sans"
              style={{
                minHeight: 44,
                paddingVertical: 10,
                fontSize: 14,
                color: colors.text,
              }}
            />
          </MoonFieldInput>
        </MoonFormField>
      ) : null}

      <MoonFormField
        label={t("you.connections_sharing.connection_modal.practice_name")}
        labelIcon={Building2}
        style={{ marginTop: 16 }}
      >
        <MoonFieldInput>
          <TextInput
            value={practiceName}
            onChangeText={setPracticeName}
            accessibilityLabel={t(
              "you.connections_sharing.connection_modal.practice_name",
            )}
            placeholder={t(
              "you.connections_sharing.connection_modal.practice_name_placeholder",
            )}
            placeholderTextColor={colors.textFaint}
            autoCapitalize="words"
            returnKeyType="next"
            editable={!isBusy}
            className="font-dm-sans"
            style={{
              minHeight: 44,
              paddingVertical: 10,
              fontSize: 14,
              color: colors.text,
            }}
          />
        </MoonFieldInput>
      </MoonFormField>

      <MoonFormField
        label={t("you.connections_sharing.connection_modal.location")}
        labelIcon={MapPin}
        style={{ marginTop: 16 }}
      >
        <MoonFieldInput>
          <TextInput
            value={location}
            onChangeText={setLocation}
            accessibilityLabel={t(
              "you.connections_sharing.connection_modal.location",
            )}
            placeholder={t(
              "you.connections_sharing.connection_modal.location_placeholder",
            )}
            placeholderTextColor={colors.textFaint}
            autoCapitalize="words"
            returnKeyType="next"
            editable={!isBusy}
            className="font-dm-sans"
            style={{
              minHeight: 44,
              paddingVertical: 10,
              fontSize: 14,
              color: colors.text,
            }}
          />
        </MoonFieldInput>
      </MoonFormField>

      <MoonFormField
        label={t("you.connections_sharing.connection_modal.contact_phone")}
        labelIcon={Phone}
        style={{ marginTop: 16 }}
      >
        <MoonFieldInput>
          <TextInput
            value={contactPhone}
            onChangeText={setContactPhone}
            accessibilityLabel={t(
              "you.connections_sharing.connection_modal.contact_phone",
            )}
            placeholder={t(
              "you.connections_sharing.connection_modal.contact_phone_placeholder",
            )}
            placeholderTextColor={colors.textFaint}
            keyboardType="phone-pad"
            returnKeyType="next"
            editable={!isBusy}
            className="font-dm-sans"
            style={{
              minHeight: 44,
              paddingVertical: 10,
              fontSize: 14,
              color: colors.text,
            }}
          />
        </MoonFieldInput>
      </MoonFormField>

      <MoonFormField
        label={t("you.connections_sharing.connection_modal.contact_email")}
        labelIcon={Mail}
        labelHint={t(
          "you.connections_sharing.connection_modal.contact_email_hint",
        )}
        error={emailError}
        style={{ marginTop: 16 }}
      >
        <MoonFieldInput hasError={!!emailError}>
          <TextInput
            value={contactEmail}
            onChangeText={(value) => {
              setContactEmail(value);
              setActionError(null);
            }}
            accessibilityLabel={t(
              "you.connections_sharing.connection_modal.contact_email",
            )}
            placeholder={t(
              "you.connections_sharing.connection_modal.contact_email_placeholder",
            )}
            placeholderTextColor={colors.textFaint}
            autoCapitalize="none"
            autoCorrect={false}
            keyboardType="email-address"
            autoComplete="email"
            returnKeyType={kind === "provider" ? "next" : "done"}
            editable={!isBusy}
            className="font-dm-sans"
            style={{
              minHeight: 44,
              paddingVertical: 10,
              fontSize: 14,
              color: colors.text,
            }}
          />
        </MoonFieldInput>
      </MoonFormField>

      {kind === "provider" ? (
        <MoonFormField
          label={t("you.connections_sharing.connection_modal.specialty")}
          labelIcon={Stethoscope}
          style={{ marginTop: 16 }}
        >
          <MoonFieldInput>
            <TextInput
              value={specialty}
              onChangeText={setSpecialty}
              accessibilityLabel={t(
                "you.connections_sharing.connection_modal.specialty",
              )}
              placeholder={t(
                "you.connections_sharing.connection_modal.specialty_placeholder",
              )}
              placeholderTextColor={colors.textFaint}
              autoCapitalize="words"
              returnKeyType="next"
              editable={!isBusy}
              className="font-dm-sans"
              style={{
                minHeight: 44,
                paddingVertical: 10,
                fontSize: 14,
                color: colors.text,
              }}
            />
          </MoonFieldInput>
        </MoonFormField>
      ) : null}

      <MoonFormField
        label={t("you.connections_sharing.connection_modal.notes")}
        labelIcon={FileText}
        style={{ marginTop: 16 }}
      >
        <MoonFieldInput>
          <TextInput
            value={notes}
            onChangeText={setNotes}
            accessibilityLabel={t(
              "you.connections_sharing.connection_modal.notes",
            )}
            placeholder={t(
              "you.connections_sharing.connection_modal.notes_placeholder",
            )}
            placeholderTextColor={colors.textFaint}
            multiline
            editable={!isBusy}
            className="font-dm-sans"
            style={{
              minHeight: 72,
              paddingVertical: 10,
              fontSize: 14,
              color: colors.text,
              textAlignVertical: "top",
            }}
          />
        </MoonFieldInput>
      </MoonFormField>

      {actionError ? (
        <View style={{ marginTop: 16 }}>
          <ActionBanner
            variant="important"
            message={t(
              `you.connections_sharing.connection_modal.${actionError}_failed`,
            )}
          />
        </View>
      ) : null}

      <Text
        className="font-dm-sans"
        style={{
          fontSize: 12,
          lineHeight: 16,
          color: EMBER.colors.textFaint,
          textAlign: "center",
          marginTop: 18,
        }}
      >
        {t("you.connections_sharing.connection_modal.privacy_note")}
      </Text>

      <View style={{ flexDirection: "row", gap: 10, marginTop: 20 }}>
        <SoftSecondaryButton
          label={t(
            connections.length > 0
              ? "you.connections_sharing.connection_modal.back"
              : "you.connections_sharing.connection_modal.cancel",
          )}
          onPress={connections.length > 0 ? returnToManagement : handleClose}
          disabled={isBusy}
          flex={1}
        />
        <DialogPrimaryButton
          label={t("you.connections_sharing.connection_modal.save")}
          onPress={handleSave}
          disabled={isBusy}
          loading={isSaving}
          flex={1}
        />
      </View>

      {selectedConnection ? (
        <TextActionButton
          label={t(`${managementKey(kind)}.remove`)}
          onPress={() => {
            setActionError(null);
            setView("confirm-delete");
          }}
          disabled={isBusy}
          color={colors.coral}
          style={{ alignSelf: "center", marginTop: 8 }}
        />
      ) : null}
    </ScrollView>
  );

  const renderDeleteConfirmation = () => (
    <>
      <DialogIcon source={icon} width={50} height={58} />
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 20,
          lineHeight: 25,
          fontWeight: "600",
          color: EMBER.colors.text,
          textAlign: "center",
          marginTop: 8,
          marginBottom: 6,
        }}
      >
        {t("you.connections_sharing.connection_management.remove_title", {
          params: { name: selectedConnection?.name ?? "" },
        })}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 14,
          lineHeight: 20,
          color: EMBER.colors.textLight,
          textAlign: "center",
          marginBottom: 20,
        }}
      >
        {t("you.connections_sharing.connection_management.remove_description")}
      </Text>

      {actionError === "delete" ? (
        <View style={{ marginBottom: 16 }}>
          <ActionBanner
            variant="important"
            message={t(
              "you.connections_sharing.connection_modal.delete_failed",
            )}
          />
        </View>
      ) : null}

      <View style={{ flexDirection: "row", gap: 10 }}>
        <SoftSecondaryButton
          label={t("you.connections_sharing.connection_modal.cancel")}
          onPress={() => setView("form")}
          disabled={isBusy}
          flex={1}
        />
        <DialogPrimaryButton
          label={t(
            "you.connections_sharing.connection_management.remove_confirm",
          )}
          onPress={handleDelete}
          loading={isDeleting}
          disabled={isBusy}
          flex={1}
          textStyle={{ color: "#FFFFFF" }}
        />
      </View>
    </>
  );

  const renderSuccess = () => {
    if (!recentlySaved) return renderManagement();
    return (
      <>
        <DialogIcon source={icon} width={50} height={58} />
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 20,
            lineHeight: 25,
            fontWeight: "600",
            color: EMBER.colors.text,
            textAlign: "center",
            marginTop: 8,
            marginBottom: 6,
          }}
        >
          {t("you.connections_sharing.connection_modal.saved_title", {
            params: { name: recentlySaved.name },
          })}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 14,
            lineHeight: 20,
            color: EMBER.colors.textLight,
            textAlign: "center",
            marginBottom: 18,
          }}
        >
          {t("you.connections_sharing.connection_modal.saved_description")}
        </Text>
        <DialogPrimaryButton
          label={t("you.connections_sharing.connection_modal.done")}
          onPress={returnToManagement}
          fullWidth
        />
      </>
    );
  };

  return (
    <Dialog
      visible={visible}
      onClose={handleClose}
      layout="centered"
      closeButton="with-background"
      dismissOnBackdropTap={!isBusy}
      contentStyle={{
        paddingTop: 28,
        paddingHorizontal: 24,
        paddingBottom: 24,
      }}
      testID="care-connection-modal"
    >
      {view === "manage"
        ? renderManagement()
        : view === "confirm-delete"
          ? renderDeleteConfirmation()
          : view === "success"
            ? renderSuccess()
            : renderForm()}
    </Dialog>
  );
}
